import { useMemo, useRef, useState } from "react";
import SegmentModal from "../../../CommonLandingDT/SegmentModal";
import FormattedText, {
  getPlainText,
} from "../../../FormattedText/FormattedText";
import LandingButton from "../../../LandingButton/LandingButton";
import type {
  Country,
  PricingView,
  Segment,
  TravelInsuranceTab,
} from "../../CommonLandingMweb.types";
import { COMMON_LANDING_CONSTANTS } from "../../constants";
import { AnnualPlanBottomSheet as AnnualPlanBottomSheetComponent } from "../AnnualPlanBottomSheet";
import { CalendarBottomSheet as CalendarBottomSheetComponent } from "../CalendarBottomSheet";
import { CarouselPersuasion } from "../CarouselPersuasion";
import { CountrySelector } from "../CountrySelector";
import { DateRangeSelector } from "../DateRangeSelector";
import { Persuasion } from "../Persuasion";
import { PlanTypeSelector } from "../PlanTypeSelector";
import { TravellerSelector } from "../TravellerSelector";
import { TripOptions } from "../TripOptions";
import {
  Banner,
  BannerClose,
  Container,
  FieldError,
  FooterStripContent,
  FooterStripGradientBorder,
  FooterStripWrapper,
  ValidationToast,
} from "./Styles";
import { parseUnix } from "./urlUtils";
import { useTravelInsuranceFormActions } from "./useTravelInsuranceFormActions";
import { useTravelInsuranceUrlSync } from "./useTravelInsuranceUrlSync";

interface TravelInsuranceCardProps {
  travelInsuranceData?: TravelInsuranceTab;
  onCountryChange?: (countries: Country[]) => void;
  onDateChange?: (from: Date, to: Date) => void;
  onTravellerChange?: (count: number) => void;
  onSegmentChange?: (segments: Segment[]) => void;
  onTripOptionChange?: (tripOptionId: string | null) => void;
  onCTAPress?: () => void;
}

export const TravelInsuranceCard = ({
  travelInsuranceData,
  onCountryChange,
  onDateChange,
  onTravellerChange,
  onSegmentChange,
  onTripOptionChange,
  onCTAPress,
}: TravelInsuranceCardProps) => {
  const tripOptions = travelInsuranceData?.tripOptions || [];
  const singleTripOptionId =
    tripOptions.length === 1 ? tripOptions[0]?.id ?? null : null;
  const initialTripOption =
    tripOptions.length < 2
      ? singleTripOptionId
      : travelInsuranceData?.prePopulatedData?.tripOption ||
        tripOptions.find((option) => option?.isSelected)?.id ||
        tripOptions[0]?.id ||
        null;

  const initialStartDate =
    parseUnix(
      travelInsuranceData?.prePopulatedData?.travelDuration?.startDate
    ) || new Date();
  const initialEndDate =
    parseUnix(travelInsuranceData?.prePopulatedData?.travelDuration?.endDate) ||
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const initialTravellerCount =
    travelInsuranceData?.prePopulatedData?.travellerNum || 1;

  const [selectedTripOptionId, setSelectedTripOptionId] = useState<
    string | null
  >(initialTripOption);
  const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
  const [fromDate, setFromDate] = useState<Date>(initialStartDate);
  const [toDate, setToDate] = useState<Date>(initialEndDate);
  const [travellerCount, setTravellerCount] = useState<number>(
    initialTravellerCount
  );
  const [showAnnualPlanBottomSheet, setShowAnnualPlanBottomSheet] =
    useState(false);
  const [showCalendarBottomSheet, setShowCalendarBottomSheet] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [selectedTripOption, setSelectedTripOption] = useState<any>(null);
  const [planSegmentId, setPlanSegmentId] = useState<string | null>(null);
  const [pricingViewId, setPricingViewId] = useState<string | null>(null);

  const onCountryChangeRef = useRef(onCountryChange);
  const onDateChangeRef = useRef(onDateChange);
  const onTravellerChangeRef = useRef(onTravellerChange);
  const onSegmentChangeRef = useRef(onSegmentChange);
  const onTripOptionChangeRef = useRef(onTripOptionChange);
  onCountryChangeRef.current = onCountryChange;
  onDateChangeRef.current = onDateChange;
  onTravellerChangeRef.current = onTravellerChange;
  onSegmentChangeRef.current = onSegmentChange;
  onTripOptionChangeRef.current = onTripOptionChange;

  /* Derived memos */
  const resolvedTripOptionId = useMemo(() => {
    if (tripOptions.length >= 2) return selectedTripOptionId;
    if (tripOptions.length === 1) return tripOptions[0]?.id ?? null;
    return null;
  }, [tripOptions, selectedTripOptionId]);

  const currentTripOption = useMemo(
    () => tripOptions.find((option) => option.id === resolvedTripOptionId),
    [tripOptions, resolvedTripOptionId]
  );

  const planSegments = useMemo(() => {
    const raw = (currentTripOption?.planTypes?.value || []).map(
      (segment: Segment) => ({
        ...segment,
        isSelected: Boolean(segment.isSelected),
      })
    );
    const selectedId =
      planSegmentId ??
      raw.find((s: Segment) => s.isSelected)?.id ??
      raw[0]?.id ??
      null;
    return raw.map((s: Segment) => ({ ...s, isSelected: s.id === selectedId }));
  }, [currentTripOption?.planTypes?.value, planSegmentId]);

  const selectedPlanSegment = useMemo(
    () => planSegments.find((s: Segment) => s.isSelected) || planSegments[0],
    [planSegments]
  );

  const pricingViews = useMemo<PricingView[]>(() => {
    const raw = currentTripOption?.pricingView?.value || [];
    if (!raw.length) return [];
    const selectedId =
      pricingViewId ??
      raw.find((p: PricingView) => p.isSelected)?.id ??
      raw[0]?.id ??
      null;
    return raw.map((p: PricingView) => ({
      ...p,
      isSelected: p.id === selectedId,
    }));
  }, [currentTripOption?.pricingView?.value, pricingViewId]);

  const calendarLimitsConfig = useMemo(() => {
    if (resolvedTripOptionId === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID)
      return currentTripOption?.config;
    return selectedPlanSegment?.config;
  }, [
    resolvedTripOptionId,
    currentTripOption?.config,
    selectedPlanSegment?.config,
  ]);

  /* URL hydration */
  useTravelInsuranceUrlSync({
    tripOptions,
    travelInsuranceData,
    initialStartDate,
    initialEndDate,
    initialTravellerCount,
    selectedTripOptionId,
    setSelectedTripOptionId,
    setSelectedCountries,
    setFromDate,
    setToDate,
    setTravellerCount,
    setPlanSegmentId,
    setPricingViewId,
    onCountryChangeRef,
    onDateChangeRef,
    onTravellerChangeRef,
    onSegmentChangeRef,
    onTripOptionChangeRef,
  });

  /* Form actions & validation */
  const {
    formCountryErrorMsg,
    formDateErrorMsg,
    formTravellerErrorMsg,
    formValidationToastMsg,
    exploreLoading,
    isSegmentModalShown,
    setIsSegmentModalShown,
    setSegmentModalHandled,
    isTripOptionModalShown,
    setIsTripOptionModalShown,
    setTripOptionModalHandled,
    handleOptionSelect,
    handleCountryChange,
    handleDateChange,
    handleTravellerChange,
    handleSegmentChange,
    handlePricingViewChange,
    handleExplorePlansClick,
  } = useTravelInsuranceFormActions({
    resolvedTripOptionId,
    currentTripOption,
    selectedPlanSegment,
    pricingViewId,
    fromDate,
    toDate,
    travellerCount,
    selectedCountriesCount: selectedCountries.length,
    segments: planSegments,
    travellerMin: travelInsuranceData?.travelSearchForm?.traveller?.min,
    travellerMax: travelInsuranceData?.travelSearchForm?.traveller?.max,
    travCountError:
      travelInsuranceData?.travelSearchForm?.traveller?.travCountError,
    setSelectedTripOptionId,
    setSelectedTripOption,
    setSelectedCountries,
    setFromDate,
    setToDate,
    setTravellerCount,
    setPlanSegmentId,
    setPricingViewId,
    onCountryChange,
    onDateChange,
    onTravellerChange,
    onSegmentChange,
    onTripOptionChange,
    onCTAPress,
  });

  return (
    <Container>
      {formValidationToastMsg ? (
        <ValidationToast role="status">
          {formValidationToastMsg}
        </ValidationToast>
      ) : null}
      <Persuasion persuasion={travelInsuranceData?.persuasion} />
      {travelInsuranceData?.topBanner?.title && !isBannerDismissed ? (
        <Banner>
          <FormattedText value={travelInsuranceData.topBanner.title} />
          <BannerClose onClick={() => setIsBannerDismissed(true)}>
            x
          </BannerClose>
        </Banner>
      ) : null}

      {tripOptions.length >= 2 ? (
        <TripOptions
          tripOptions={tripOptions}
          selectedOptionId={selectedTripOptionId}
          onOptionSelect={handleOptionSelect}
          onInfoPress={(option) => {
            setSelectedTripOption(option);
            setShowAnnualPlanBottomSheet(true);
          }}
        />
      ) : null}

      <CountrySelector
        countryList={currentTripOption?.countryList}
        initialSelectedCountries={selectedCountries}
        onCountryChange={handleCountryChange}
        errorText={formCountryErrorMsg || undefined}
      />

      <DateRangeSelector
        startDate={fromDate}
        endDate={toDate}
        onDateChange={handleDateChange}
        isAnnualMultitrip={
          resolvedTripOptionId === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID
        }
        calendarConfig={calendarLimitsConfig}
        calenderSelectionType={currentTripOption?.calenderSelectionType}
      />
      {formDateErrorMsg ? <FieldError>{formDateErrorMsg}</FieldError> : null}

      <TravellerSelector
        travellerInfo={travelInsuranceData?.travelSearchForm?.traveller}
        travellerCount={travellerCount}
        onTravellerCountChange={handleTravellerChange}
      />
      {formTravellerErrorMsg ? (
        <FieldError>{formTravellerErrorMsg}</FieldError>
      ) : null}

      {planSegments.length > 0 ? (
        <PlanTypeSelector
          segments={planSegments}
          label={currentTripOption?.planTypes?.label}
          onSegmentSelect={handleSegmentChange}
        />
      ) : null}

      {pricingViews.length > 0 ? (
        <PlanTypeSelector
          segments={pricingViews}
          label={currentTripOption?.pricingView?.label}
          onSegmentSelect={handlePricingViewChange}
        />
      ) : null}

      <LandingButton
        customTitle={
          getPlainText(travelInsuranceData?.travelSearchForm?.ctaText?.title) ||
          "Explore Plans"
        }
        customWidth="100%"
        customHeight="44px"
        isLoading={exploreLoading}
        shadowVariant="none"
        onClick={() => void handleExplorePlansClick()}
      />

      <CarouselPersuasion
        carouselData={travelInsuranceData?.travelSearchForm?.carouselPersuasion}
      />

      {travelInsuranceData?.travelSearchForm?.footerSection?.description ? (
        <FooterStripWrapper>
          <FooterStripGradientBorder />
          <FooterStripContent>
            <FormattedText
              value={
                travelInsuranceData.travelSearchForm.footerSection.description
              }
            />
          </FooterStripContent>
        </FooterStripWrapper>
      ) : null}

      <AnnualPlanBottomSheetComponent
        visible={showAnnualPlanBottomSheet}
        setVisible={setShowAnnualPlanBottomSheet}
        onExplorePress={() => {
          setShowAnnualPlanBottomSheet(false);
          if (selectedTripOption?.id) handleOptionSelect(selectedTripOption);
        }}
        bottomSheetData={selectedTripOption?.bottomSheetData}
      />

      <CalendarBottomSheetComponent
        visible={showCalendarBottomSheet}
        modalData={travelInsuranceData?.calendarBottomSheet?.modal}
        onGoBack={() => setShowCalendarBottomSheet(false)}
        onTryAnnualPlan={() => setShowCalendarBottomSheet(false)}
      />

      {isSegmentModalShown && selectedPlanSegment?.modal ? (
        <SegmentModal
          modal={selectedPlanSegment.modal as any}
          onClose={() => {
            setIsSegmentModalShown(false);
            setSegmentModalHandled(false);
          }}
          onPrimaryCta={() => void handleExplorePlansClick()}
          onSecondaryCta={() => {
            setIsSegmentModalShown(false);
            setSegmentModalHandled(false);
          }}
        />
      ) : null}

      {isTripOptionModalShown && currentTripOption?.modal ? (
        <SegmentModal
          modal={currentTripOption.modal as any}
          onClose={() => {
            setIsTripOptionModalShown(false);
            setTripOptionModalHandled(false);
          }}
          onPrimaryCta={() => void handleExplorePlansClick()}
          onSecondaryCta={() => {
            setIsTripOptionModalShown(false);
            setTripOptionModalHandled(false);
          }}
        />
      ) : null}
    </Container>
  );
};
